import { useState } from 'react';
import { Splide } from '@splidejs/react-splide';

import { ArrowRight } from '@/icons';
import { IconButton } from '@/molecules';

import { toArray } from '@/lib/utils';

import type { Options } from '@splidejs/splide';
import type { FunctionComponent } from 'react';
import type { TypeSliderHeroProps } from './types';

/**
 * SliderHero
 */
export const SliderHero: FunctionComponent<TypeSliderHeroProps> = ({
  className = '',
  minHeight = '',
  children,
}: TypeSliderHeroProps) => {
  const childrenArray = toArray(children);
  const [slideConfig, setSlideConfig] = useState({
    caption: '',
    negative: true,
    index: 1,
    total: childrenArray.length,
  });
  const options: Options = {
    type: childrenArray.length === 1 ? 'slide' : 'loop',
    perPage: 1,
    perMove: 1,
    pagination: slideConfig.total > 1,
    drag: slideConfig.total > 1,
    arrows: slideConfig.total > 1,
    classes: {
      pagination: `u-wrapper flex bottom-[37px] z-10 justify-center gap-x-4 px-4 md:bottom-[60px] md:justify-start xl:px-0 [&>li]:flex [&>li]:h-5 [&>li]:w-5 [&>li]:items-center [&>li]:justify-center [&_li:has(button[aria-current="true"])]:bg-transparent [&_li:has(button[aria-current="true"])]:bg-center [&_li:has(button[aria-current="true"])]:bg-no-repeat [&_li:has(button[aria-current="true"])]:bg-[url('/assets/images/selected.svg')]`,
      page: `m-0 h-1.5 w-1.5 scale-100 rounded-full bg-typo-alternative opacity-100 aria-[current="true"]:cursor-default ${slideConfig.negative ? 'bg-typo-primary' : ''}`,
    },
    lazyLoad: 'nearby',
    autoplay: slideConfig.total > 1,
    /**
     * Important! if the speed is changed you mush change the
     * CSS transition speed to the same value
     */
    speed: 400,
  };
  const updateSlideConfig = (
    slide: HTMLElement,
    index: number,
    total: number,
  ) =>
    setSlideConfig({
      caption: slide?.getAttribute('data-caption') ?? '',
      negative: slide?.getAttribute('data-negative') === 'true' ? true : false,
      index: index,
      total: total,
    });

  return (
    <>
      {/**
       * TODO: La versión que tenemos de Splide no tiene el prop Children ya que en React 17 se añadia automaticamente pero en la 18 no
       * Cuando actualicemos Splide a la última versión, podemos quitar el @ts-ignore
       * @eslint-disable-next-line @typescript-eslint/ban-ts-comment
       * @ts-expect-error types are not for react 18 */}
      <Splide
        options={options}
        className={`visible relative ${className} ${minHeight}`}
        onMove={(splide, index) => {
          const slide = splide.Components.Elements.slides[index];
          if (slide) {
            updateSlideConfig(slide, index + 1, splide.length);
          }
        }}
        onMounted={(splide) => {
          const slide = splide.Components.Elements.slides[0];
          if (slide) {
            updateSlideConfig(slide, 1, splide.length);
          }
        }}
        onPaginationUpdated={(_, __, prev, curr) => {
          if (prev?.li?.className) prev.li.className = '';
          if (curr?.li?.className) curr.li.className = 'current_slide_li';
        }}
        {...(slideConfig.total > 1
          ? {
              renderControls: () => (
                <div className="splide__arrows u-wrapper absolute inset-x-0 z-20 hidden md:block">
                  <IconButton
                    className="splide_arrow splide__arrow--next absolute bottom-10 z-10 mr-0"
                    size="medium"
                    style="solid"
                    aria-label="Slide Siguiente"
                  >
                    <ArrowRight color="#FFFFFF" />
                  </IconButton>

                  <IconButton
                    className="splide_arrow splide__arrow--prev absolute bottom-10 right-[66px] z-10 ml-auto"
                    size="medium"
                    style="solid"
                    aria-label="Slide Anterior"
                  >
                    <ArrowRight color="#FFFFFF" />
                  </IconButton>
                </div>
              ),
            }
          : {})}
      >
        {childrenArray}
      </Splide>
    </>
  );
};
